import { Check } from 'lucide-react'
import type { FirstWeekStep, FirstWeekStepKey } from 'aeo-schema-generator-shared/types'
import { Button, Status } from '@/design-system/primitives'
import { cn } from '@/utils/cn'
import '@/design-system/first-week.css'
import { CHECK_AGAIN, DO_THIS_NOW, IN_FLIGHT, NOT_SEEN_YET, WAITING_ON_INVITE, evidenceLine, liveStepKey, stepCopy } from './firstWeekCopy'

/*
  One step of the first week. The disc says where it stands, the title says
  what it is, one line says what it is for, one says where its result shows
  up, and the last says how much effort it is. Only the current step gets
  the primary button. Colour means status: blue is done, ink is now, grey is
  waiting.

  A step whose schema was put on the page but not seen there yet offers
  "Check again" wherever it sits: as the primary button when it is the
  current step, as the quiet link otherwise. "Do this now" would be the
  wrong verb for something already done once.

  On the free guide the Watch step leads to plans, so its quiet link says
  "See plans", the same words its button carries, instead of "Do this now".

  A step this person cannot carry out is still listed, so they can see where
  the guide stands, and it offers them neither the button nor the link.
*/
export function FirstWeekStepRow({
  step,
  index,
  isCurrent,
  inFlight,
  onAct,
  variant = 'trial',
  canAct = true,
  installationMethod = 'hubspot',
}: {
  step: FirstWeekStep
  index: number
  isCurrent: boolean
  inFlight: boolean
  onAct: (key: FirstWeekStepKey) => void
  /** Which guide this row is in. A free account's Watch step reads differently. */
  variant?: 'trial' | 'free'
  /** False when the step is somebody else's to do: the row then offers nothing to press. */
  canAct?: boolean
  installationMethod?: 'hubspot' | 'manual'
}) {
  const copy = stepCopy(step.key, variant, installationMethod)
  const done = step.state === 'done'
  const notSeen = step.state === 'not_seen_yet'
  // The buttons repeat across rows ("Do this now" four times), so each one
  // points a screen reader at its own step's title.
  const titleId = `fw-step-${step.key}`
  const quietLabel = variant === 'free' && step.key === 'watch' ? copy.action : DO_THIS_NOW

  return (
    <li
      className={cn('ds-fw__row', done && 'ds-fw__row--done', isCurrent && 'ds-fw__row--now')}
      data-step={step.key}
      data-state={step.state}
      aria-current={isCurrent ? 'step' : undefined}
    >
      <span className="ds-fw__disc" aria-hidden="true">
        {done ? <Check size={16} /> : index + 1}
      </span>
      <div className="ds-fw__body">
        <div className="ds-fw__title" id={titleId}>
          {copy.title}
        </div>
        {done && step.evidence ? (
          <Status kind="good" size="sm">
            {evidenceLine(step.evidence.at, step.evidence.byName, copy.doneLabel)}
          </Status>
        ) : (
          <p className="ds-fw__line">{copy.line}</p>
        )}
        {!done && step.key === liveStepKey(variant) && inFlight && <p className="ds-fw__state">{IN_FLIGHT}</p>}
        {!done && notSeen && <p className="ds-fw__state">{NOT_SEEN_YET}</p>}
        {!done && step.key === 'invite' && step.evidence && <p className="ds-fw__state">{WAITING_ON_INVITE(step.evidence.at)}</p>}
        {!done && <p className="ds-fw__where">{copy.where}</p>}
        {!done && !isCurrent && <p className="ds-fw__effort">{copy.effort}</p>}
      </div>
      <div className="ds-fw__act">
        {canAct && isCurrent && !inFlight && (
          <Button variant="primary" onClick={() => onAct(step.key)} aria-describedby={titleId}>
            {notSeen ? CHECK_AGAIN : copy.action}
          </Button>
        )}
        {canAct && !done && !isCurrent && !inFlight && (
          <button type="button" className="ds-fw__now" onClick={() => onAct(step.key)} aria-describedby={titleId}>
            {notSeen ? CHECK_AGAIN : quietLabel}
          </button>
        )}
      </div>
    </li>
  )
}
